import * as React from 'react';
import { Info, TriangleAlert, type LucideIcon } from 'lucide-react';
import { cn } from '../../lib/utils';

export type NoteTone = 'muted' | 'info' | 'warning' | 'critical';

export interface NoteProps extends React.HTMLAttributes<HTMLDivElement> {
  tone?: NoteTone;
  /** Heading for a note that lists several things (e.g. validation errors). */
  title?: string;
}

const toneClass: Record<NoteTone, string> = {
  muted: 'text-muted-foreground',
  info: 'text-muted-foreground',
  // Something is stuck and needs attention, but nothing is broken — red would
  // overstate it, and on a settings page it competes with the save bar.
  warning: 'text-amber-700',
  critical: 'text-destructive',
};

const iconFor: Partial<Record<NoteTone, LucideIcon>> = {
  info: Info,
  warning: TriangleAlert,
  critical: TriangleAlert,
};

/**
 * Guidance or a problem stated *inside* a panel.
 *
 * An Alert is a card — border, shadow, tinted icon chip — and putting one
 * inside a section card produced a card inside a card for a sentence of
 * explanation. A note is the sentence: small, quiet, aligned with the fields it
 * is talking about. Alerts stay for page-level messages, under the heading.
 */
export function Note({ tone = 'muted', title, className, children, ...props }: NoteProps) {
  const Icon = iconFor[tone];
  return (
    <div
      className={cn('flex gap-2 text-xs leading-5', toneClass[tone], className)}
      {...(tone === 'critical' ? { role: 'alert' } : {})}
      {...props}
    >
      {Icon && <Icon aria-hidden="true" className="mt-0.5 h-3.5 w-3.5 shrink-0" />}
      <div className="min-w-0">
        {title && <p className="font-semibold">{title}</p>}
        {children}
      </div>
    </div>
  );
}
